import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Loader2, RefreshCw, Upload } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { fetchPlanSynchronizacji, synchronizujObrazki, zlecSpisObrazkow } from '@/lib/items-api';
import { cn } from '@/lib/utils';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  SYNCHRONIZUJ WSZYSTKO — wyrównanie katalogów grafik na automatach.
 *
 *  ⚠️⚠️ ZAMÓWIONE 07.09.2026: „trzeba też jakiegoś przycisku «synchronizuj wszystko» i powinna być
 *  lista z zaznaczonym wszystkim i ma lecieć z mastera w dół — oczywiście ostrzeżenia jak nie ma
 *  jakichś plików na masterze". Domknięte tego samego dnia: „opcja jeden, ale jakoś tak, żeby na
 *  początku albo skanowała, albo wręcz wymagała skanu zanim zacznie coś wysyłać".
 *
 *  ⚠️⚠️ SKAN JEST WARUNKIEM, NIE OZDOBĄ — i to jest sedno tego okna. Wysyłamy TYLKO tam, gdzie
 *  pliku brakuje, a „gdzie brakuje" wie wyłącznie spis katalogu. Maszyna bez pomiaru nie jest więc
 *  maszyną z pustym katalogiem: dopóki jakakolwiek nie ma spisu, wysyłka jest ZABLOKOWANA, a nie
 *  wykonana „na wszelki wypadek". Instancja pilnuje tego drugi raz u siebie.
 *
 *  ⚠️⚠️ CZEGO TO NIE NAPRAWIA, I MÓWIMY TO NA EKRANIE: wysyłka wkłada plik do katalogu i nie dotyka
 *  bazy maszyny. Automat, który u siebie wskazuje INNY plik (żółty rozjazd w tabeli pokrycia),
 *  zostanie po synchronizacji nadal rozjechany — to wskaźnik w jego bazie, robota config-down.
 *  Obiecanie tu naprawy rozjazdu byłoby dokładnie tą klasą ślepoty, którą naprawiamy w tym module.
 *
 *  ⚠️ JEDNOSTKĄ JEST PLIK, NIE TOWAR. Ten sam indeks dostawcy obsługuje po kilka towarów (te same
 *  rękawice w R9 i R10), więc lista jest krótsza od tabeli pokrycia i jedna pozycja naprawia kilka
 *  ramek naraz — dlatego przy każdym pliku stoi, ilu towarów dotyczy.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function SynchronizujObrazki({ groupId, onClose }: { groupId: string; onClose: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const [odznaczone, setOdznaczone] = useState<ReadonlySet<string>>(new Set());
  /**
   * ⚠️ ZGODA NA POMINIĘCIE MASZYNY BEZ POMIARU — domyślnie WYŁĄCZONA. Bez niej jedna maszyna, która
   * nie odpowiada na spis, blokowałaby synchronizację całej wyspy; z nią włączoną po cichu
   * pomijalibyśmy tę, która może potrzebować plików najbardziej. Dlatego to jawny klik.
   */
  const [pomijaj, setPomijaj] = useState(false);

  const plan = useQuery({
    queryKey: ['plan-synchronizacji', groupId],
    queryFn: () => fetchPlanSynchronizacji(groupId),
    /**
     * ⚠️ ODŚWIEŻANIE W TRAKCIE SKANU. Spis wykonuje agent w swoim cyklu, więc plan zmienia się
     * dopiero po jego powrocie — bez tego okno pokazywałoby „brak pomiaru" długo po skanie.
     */
    refetchInterval: (q) => (q.state.data !== undefined && q.state.data.niezmierzoneWezly.length > 0 ? 10_000 : false),
  });

  const spis = useMutation({
    mutationFn: () => zlecSpisObrazkow(groupId),
    onSuccess: (r) => {
      if (r.zadan > 0) {
        toast.success(t('zdj.spisZlecony').replace('{zadan}', String(r.zadan)).replace('{wezlow}', String(r.wezlow)));
      } else {
        toast.error(t('zdj.spisNikt'));
      }
      void qc.invalidateQueries({ queryKey: ['plan-synchronizacji'] });
      void qc.invalidateQueries({ queryKey: ['pokrycie-obrazkow'] });
    },
    onError: (e: Error) => toast.error(e instanceof ApiError ? e.message : String(e)),
  });

  const wyslij = useMutation({
    mutationFn: (pliki: readonly string[]) => synchronizujObrazki(groupId, pliki, pomijaj),
    onSuccess: (r) => {
      if (r.zadan === 0) {
        toast.toast(t('obraz.syn.nicDoWyslania'));
      } else {
        toast.success(
          t('obraz.syn.zlecone')
            .replace('{zadan}', String(r.zadan))
            .replace('{plikow}', String(r.plikow))
            .replace('{automatow}', String(r.automatow)),
        );
      }
      /** ⚠️ Pominięte wymieniamy z nazwy — cisza czytałaby się jak „wszystko poszło". */
      if (r.pominietePliki.length > 0) {
        toast.error(t('obraz.syn.pominieteBezBajtow').replace('{pliki}', r.pominietePliki.join(', ')));
      }
      void qc.invalidateQueries({ queryKey: ['plan-synchronizacji'] });
      void qc.invalidateQueries({ queryKey: ['pokrycie-obrazkow'] });
      if (r.zadan > 0) onClose();
    },
    onError: (e: Error) => toast.error(e instanceof ApiError ? e.message : String(e)),
  });

  const d = plan.data;
  /** Pliki, które da się wysłać: master ma bajty i gdzieś ich brakuje. */
  const doRoboty = useMemo(() => (d?.pliki ?? []).filter((p) => p.wBibliotece && p.doWyslania.length > 0), [d]);
  const zaznaczone = useMemo(() => doRoboty.filter((p) => !odznaczone.has(p.nazwaPliku)), [doRoboty, odznaczone]);
  const par = zaznaczone.reduce((s, p) => s + p.doWyslania.length, 0);
  const brakPomiaru = (d?.niezmierzoneWezly ?? []).length > 0;
  const zaDuzo = d !== undefined && par > d.maksParNaRaz;

  /**
   * ⚠️ ODZNACZENIA CZYŚCIMY PO PRZELICZENIU PLANU. Po skanie albo po wysyłce lista pozycji jest
   * inna; trzymanie starych odznaczeń znaczyłoby, że człowiek pomija plik, którego już nie widać.
   */
  useEffect(() => {
    setOdznaczone(new Set());
  }, [d?.parDoWyslania]);

  const przelacz = (nazwa: string) =>
    setOdznaczone((s) => {
      const n = new Set(s);
      if (n.has(nazwa)) n.delete(nazwa);
      else n.add(nazwa);
      return n;
    });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[min(48rem,calc(100vw-2rem))]" title={t('obraz.syn.tytul')}>
        {plan.isPending && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('common.loading')}
          </p>
        )}
        {plan.isError && <p className="text-sm text-[var(--destructive)]">{String(plan.error)}</p>}

        {d !== undefined && (
          <>
            {/*
              ══════════════════════════════════════════════════════════════════
               ⚠️⚠️ BRAMKA SKANU. Dopóki jakakolwiek maszyna nie ma spisu, nie ma z czego policzyć,
               czego jej brakuje — więc wysyłka jest zablokowana i widać nazwy tych maszyn.
               Skan jest darmowy w bajtach (sam odczyt katalogu), ale wraca dopiero w cyklu agenta,
               dlatego okno samo się odświeża.
              ══════════════════════════════════════════════════════════════════
            */}
            {brakPomiaru && (
              <div className="space-y-2 rounded-md border border-[var(--destructive)]/40 bg-[var(--destructive)]/5 p-2.5">
                <p className="flex items-start gap-1.5 text-xs">
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-[var(--destructive)]" />
                  <span>
                    {t('obraz.syn.bezPomiaru').replace('{maszyny}', d.niezmierzoneWezly.map((w) => w.nazwa).join(', '))}
                  </span>
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <Button size="sm" variant="outline" disabled={spis.isPending} onClick={() => spis.mutate()}>
                    {spis.isPending ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                    {t('zdj.spis')}
                  </Button>
                  <label className="flex items-center gap-1.5 text-xs">
                    <input type="checkbox" checked={pomijaj} onChange={(e) => setPomijaj(e.target.checked)} />
                    {t('obraz.syn.pomijajBezPomiaru')}
                  </label>
                </div>
              </div>
            )}

            {/*
              ⚠️⚠️ ZAMÓWIONE OSTRZEŻENIE: „jak nie ma jakichś plików na masterze". Taki plik jest
              wymieniony z nazwy i NIE jedzie — nie ma czego wysłać. Lekarstwem jest zgranie
              biblioteki z automatu, który go ma, więc mówimy to wprost.
            */}
            {d.bezBajtowNaMasterze.length > 0 && (
              <p className="flex items-start gap-1.5 rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-2.5 text-xs">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-[var(--warning)]" />
                <span>
                  {t('obraz.syn.bezBajtow')
                    .replace('{n}', String(d.bezBajtowNaMasterze.length))
                    .replace('{pliki}', d.bezBajtowNaMasterze.slice(0, 8).join(', '))}
                  {d.bezBajtowNaMasterze.length > 8 && ' …'}
                </span>
              </p>
            )}

            {/* ⚠️ Wysyłka wyrównuje BAJTY W KATALOGU. Rozjazd wskaźnika w bazie maszyny to config-down. */}
            <p className="text-[11px] text-muted-foreground">{t('obraz.syn.coRobi')}</p>

            {doRoboty.length === 0 ? (
              <p className="flex items-center gap-1.5 rounded-md border border-border p-2.5 text-sm">
                <Check className="size-4 text-[var(--primary)]" />
                {t('obraz.syn.wszystkoZgodne')}
              </p>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                  <span>
                    {t('obraz.syn.podsumowanie')
                      .replace('{plikow}', String(zaznaczone.length))
                      .replace('{par}', String(par))}
                  </span>
                  <span className="flex gap-2">
                    <button type="button" className="text-[var(--primary)] hover:underline" onClick={() => setOdznaczone(new Set())}>
                      {t('obraz.syn.zaznaczWszystko')}
                    </button>
                    <button
                      type="button"
                      className="text-[var(--primary)] hover:underline"
                      onClick={() => setOdznaczone(new Set(doRoboty.map((p) => p.nazwaPliku)))}
                    >
                      {t('obraz.syn.odznaczWszystko')}
                    </button>
                  </span>
                </div>

                <ul className="max-h-[22rem] divide-y divide-border overflow-y-auto rounded-md border border-border">
                  {doRoboty.map((p) => {
                    const wybrany = !odznaczone.has(p.nazwaPliku);
                    const gdzie = p.doWyslania
                      .map((id) => d.maszyny.find((m) => m.agentId === id)?.nazwa ?? id)
                      .join(', ');
                    return (
                      <li key={p.nazwaPliku}>
                        <label className={cn('flex cursor-pointer items-start gap-2 p-2 text-xs hover:bg-accent/20', !wybrany && 'opacity-50')}>
                          <input type="checkbox" className="mt-0.5" checked={wybrany} onChange={() => przelacz(p.nazwaPliku)} />
                          <span className="min-w-0 flex-1">
                            <span className="font-mono">{p.nazwaPliku}</span>
                            <span className="block text-muted-foreground">
                              {t('obraz.syn.wiersz')
                                .replace('{maszyn}', String(p.doWyslania.length))
                                .replace('{gdzie}', gdzie)
                                .replace('{towarow}', String(p.towarow))}
                            </span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>

                {/*
                  ⚠️⚠️ LIMIT MÓWIMY PRZED WYSYŁKĄ, nie po odrzuceniu. Każda para plik→automat to
                  osobne zadanie w kolejce WSPÓLNEJ dla całej wyspy, więc granica jest realna —
                  a odznaczenie części plików to decyzja człowieka, nie nasza.
                */}
                {zaDuzo && (
                  <p className="flex items-start gap-1.5 text-xs text-[var(--destructive)]">
                    <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                    {t('obraz.syn.zaDuzoPar').replace('{par}', String(par)).replace('{limit}', String(d.maksParNaRaz))}
                  </p>
                )}
              </>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={
              d === undefined ||
              zaznaczone.length === 0 ||
              zaDuzo ||
              wyslij.isPending ||
              /** ⚠️ BRAMKA SKANU: bez pomiaru nie wysyłamy, chyba że człowiek jawnie pominął tę maszynę. */
              (brakPomiaru && !pomijaj)
            }
            onClick={() => wyslij.mutate(zaznaczone.map((p) => p.nazwaPliku))}
          >
            {wyslij.isPending ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
            {t('obraz.syn.wyslij')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
